// src/components/BlogLayout.js
import React, { useState, useEffect } from 'react';
import { fetchBlogPosts, fetchAllAuthors } from '../services/apiService';
import ArticleCard from './ArticleCard';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';

const BlogLayout = ({ title, filterType, filterValue }) => {
  const [posts, setPosts] = useState([]); // Will hold only the posts for the current page
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // Will be set from the API response
  const postsPerPage = 9;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch only the data needed for the current page
        const { posts: postsData, totalPages: totalPagesFromApi } = await fetchBlogPosts({
          page: currentPage,
          perPage: postsPerPage,
        });
        
        // This client-side filtering is NOT ideal for performance on category/author pages,
        // but it's a direct refactor. A future optimization is to pass category/author IDs to the API.
        let finalPosts = postsData;
        if (filterType === 'category') {
            finalPosts = (await fetchBlogPosts({ perPage: 100 })).posts.filter(p => p.categories.some(c => c.name.toLowerCase().replace(/\s+/g, '-') === filterValue));
            // This is a temporary workaround for filtering. Real pagination needs server filtering.
            setTotalPages(Math.ceil(finalPosts.length / postsPerPage));
            finalPosts = finalPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);
        } else if (filterType === 'author') {
             finalPosts = (await fetchBlogPosts({ perPage: 100 })).posts.filter(p => p.author.id === parseInt(filterValue));
             setTotalPages(Math.ceil(finalPosts.length / postsPerPage));
             finalPosts = finalPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);
             const authorsData = await fetchAllAuthors();
             const currentAuthor = authorsData.find(a => a.id === parseInt(filterValue));
             setAuthor(currentAuthor);
        } else {
            // This is for the /all-articles page, which now correctly uses server pagination
            setTotalPages(totalPagesFromApi);
        }

        setPosts(finalPosts);

      } catch (err) {
        setError('Failed to load posts. Please try again later.');
        console.error(err);
      }
      setLoading(false);
    };
    loadData();
  }, [currentPage, filterType, filterValue]); // Re-run when currentPage changes

  // Pagination logic
  const handlePrevPage = () => setCurrentPage(p => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage(p => Math.min(totalPages, p + 1));

  const getPageTitle = () => {
    if (filterType === 'category') return `Category: ${filterValue.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
    if (filterType === 'author' && author) return `Posts by ${author.name}`;
    return title;
  };

  if (loading) return <div className="text-center py-24 text-dark-text-primary">Loading posts...</div>;
  if (error) return <div className="text-center py-24 text-red-400">{error}</div>;

  return (
    <div className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">{getPageTitle()}</h1>
        </div>
        
        {posts.length > 0 ? (
          <>
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {posts.map(post => <ArticleCard key={post.id} post={post} />)}
            </div>

            {totalPages > 1 && (
              <div className="mt-16 flex justify-center items-center gap-4">
                <button onClick={handlePrevPage} disabled={currentPage === 1} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  <IoArrowBack /> Previous
                </button>
                <span className="text-sm text-dark-text-secondary">Page {currentPage} of {totalPages}</span>
                <button onClick={handleNextPage} disabled={currentPage === totalPages} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed">
                  Next <IoArrowForward />
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="mt-16 text-center text-dark-text-secondary">No posts found.</p>
        )}
      </div>
    </div>
  );
};

export default BlogLayout;